const BASE = import.meta.env.VITE_API_BASE ?? "/api";

function getToken(): string | null {
  return localStorage.getItem("cf_token");
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem("cf_token", token);
  else localStorage.removeItem("cf_token");
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { isForm?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, { method, headers, body: payload });
  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const msg = (data as any)?.detail ?? res.statusText;
    throw new ApiError(res.status, typeof msg === "string" ? msg : JSON.stringify(msg), data);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

/** Fetch a binary resource (e.g. uploaded source file) as a Blob, with auth. */
async function fetchBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { method: "GET", headers });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.blob();
}

/**
 * PUT a raw binary blob (one chunk of a chunked upload). Used by the
 * video uploader where each chunk is streamed as its own request body
 * without an enclosing FormData. We use XMLHttpRequest under the hood
 * so the caller can observe per-chunk upload progress events — a
 * 8 GB upload at 8 MB/chunk needs a smooth progress bar to feel sane.
 */
function putBlob(
  path: string,
  blob: Blob,
  opts: { signal?: AbortSignal; onProgress?: (loaded: number) => void } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getToken();
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${BASE}${path}`, true);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && opts.onProgress) opts.onProgress(ev.loaded);
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error during chunk upload"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const msg = xhr.responseText || xhr.statusText || `HTTP ${xhr.status}`;
        reject(new ApiError(xhr.status, msg));
      }
    };
    xhr.onabort = () => reject(new ApiError(0, "Upload aborted"));
    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
        return;
      }
      opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(blob);
  });
}

export const api = {
  get:    <T>(p: string)                 => request<T>("GET",    p),
  post:   <T>(p: string, body?: unknown) => request<T>("POST",   p, body),
  put:    <T>(p: string, body?: unknown) => request<T>("PUT",    p, body),
  patch:  <T>(p: string, body?: unknown) => request<T>("PATCH",  p, body),
  del:    <T>(p: string)                 => request<T>("DELETE", p),
  upload: <T>(p: string, form: FormData) => request<T>("POST",   p, form, { isForm: true }),
  blob:   (p: string)                    => fetchBlob(p),
  putBlob,
};

export interface VideoChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface VideoChatCitation {
  token: string;
  kind: "segment" | "event" | "scene";
  at_seconds: number;
  label: string;
}

export type VideoChatSseEvent =
  | { type: "delta"; text: string }
  | { type: "done"; text: string; citations: VideoChatCitation[] }
  | { type: "error"; message: string };

/**
 * POST /cases/{caseId}/videos/{videoId}/chat and return an async iterator
 * that yields parsed SSE events (delta / done / error).
 *
 * Pass an AbortSignal to cancel mid-stream.
 */
export async function* videoChatStream(
  caseId: number,
  videoId: number,
  question: string,
  history: VideoChatMessage[],
  signal?: AbortSignal,
): AsyncGenerator<VideoChatSseEvent> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(
    `${BASE}/cases/${caseId}/videos/${videoId}/chat`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ question, history }),
      signal,
    },
  );

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || res.statusText);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6).trim();
      if (!payload) continue;
      try {
        yield JSON.parse(payload) as VideoChatSseEvent;
      } catch {
        // malformed line — skip
      }
    }
  }
}

// ---------- types that mirror engine Pydantic schemas ----------

export interface User {
  id: number;
  organization_id: number;
  organization_name?: string | null;
  email: string;
  display_name: string;
  role: "owner" | "admin" | "lawyer" | "assistant" | "viewer";
  is_active: boolean;
  totp_enabled?: boolean;
}

export interface UserPage {
  items: User[];
  total: number;
  limit: number;
  offset: number;
}

export interface TotpSetupResponse {
  secret: string;
  otpauth_url: string;
  qr_data_url: string;
}

export type CaseStatus = "active" | "archived" | "closed";

/** Mirrors the `case_users.role` enum on the server. Owners/admins can
 *  manage any case in their org regardless of whether they hold a row in
 *  case_users for it. */
export type CaseMemberRole = "lead" | "collaborator" | "reviewer" | "viewer";

export interface Case {
  id: number;
  organization_id: number;
  organization_name?: string | null;
  name: string;
  slug: string;
  description?: string | null;
  matter_number?: string | null;
  status: CaseStatus;
  created_by: number;
  created_at: string;
  document_count: number;
  ready_document_count: number;
  pending_document_count: number;
  member_count: number;
  video_count: number;
  /** The caller's case_users.role on this case, if any. `null` for
   *  owners/admins who haven't been explicitly assigned (they still get
   *  full org-wide access via their account role). */
  my_role?: CaseMemberRole | null;
}

export interface CaseStatusCounts {
  all: number;
  active: number;
  archived: number;
  closed: number;
}

export interface CasePage {
  items: Case[];
  total: number;
  limit: number;
  offset: number;
  status_counts: CaseStatusCounts;
}

export type DocStatus = "queued" | "processing" | "ready" | "failed";
export type IngestProfile = "standard" | "large";

export interface Document {
  id: number;
  case_id: number;
  original_name: string;
  mime_type?: string | null;
  size_bytes: number;
  sha256: string;
  page_count?: number | null;
  ocr_applied: boolean;
  translate_requested: boolean;
  translated: boolean;
  source_language?: string | null;
  status: DocStatus;
  /** Which ingest branch ran. 'large' files use the chunked-upload +
   *  streaming path; everything else is 'standard'. */
  ingest_profile: IngestProfile;
  progress_pct: number;
  chunk_count: number;
  /** Estimated AI calls + tokens for this document's ingest (no USD).
   *  Null for older rows / before processing. */
  est_llm_calls?: number | null;
  est_input_tokens?: number | null;
  est_output_tokens?: number | null;
  /** On-demand timeline-extraction phase (separate from ingest).
   *  'idle' = never built. */
  timeline_status: TimelineStatus;
  timeline_event_count: number;
  timeline_generated_at?: string | null;
  timeline_error?: string | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

export type TimelineStatus = "idle" | "queued" | "running" | "ready" | "failed";

/** AI-call / token estimate for a document ingest (no dollar figures). */
export interface AiCallEstimate {
  accuracy: "rough" | "parsed";
  profile: IngestProfile;
  chunk_count: number;
  page_count: number;
  timeline_calls: number;
  translate_calls: number;
  vision_calls: number;
  total_calls: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  capped: boolean;
}

/** Response from POST /cases/:id/documents/uploads (chunked large upload). */
export interface DocUploadSession {
  upload_id: string;
  chunk_size: number;
  expected_chunks: number;
  received_indices: number[];
  received_bytes: number;
  estimate: AiCallEstimate;
}

export interface DocUploadStatus {
  upload_id: string;
  status: "open" | "complete" | "aborted";
  chunk_size: number;
  expected_chunks: number;
  received_indices: number[];
  received_bytes: number;
  total_bytes: number;
  document_id?: number | null;
}

export interface DocumentStatusCounts {
  all: number;
  queued: number;
  processing: number;
  ready: number;
  failed: number;
}

export interface DocumentPage {
  items: Document[];
  total: number;
  limit: number;
  offset: number;
  status_counts: DocumentStatusCounts;
}

// ---------- Videos ----------

export type VideoStatus = "uploading" | "queued" | "processing" | "ready" | "failed";
export type VideoTimelineStatus = "idle" | "queued" | "running" | "ready" | "failed";
export type VideoTimelineCategory =
  | "admission"
  | "statement"
  | "identification"
  | "action_item"
  | "contradiction"
  | "evidence"
  | "visual"
  | "other";

export interface Video {
  id: number;
  case_id: number;
  original_name: string;
  mime_type?: string | null;
  size_bytes: number;
  sha256: string;
  duration_seconds?: number | null;
  width?: number | null;
  height?: number | null;
  fps?: number | null;
  status: VideoStatus;
  progress_pct: number;
  progress_step?: string | null;
  scene_count: number;
  transcript_segment_count: number;
  error_message?: string | null;
  transcribe_provider?: string | null;
  /** AI transcript translation (opt-in at upload). `translated` is true only
   *  when a non-English transcript was actually translated to English. */
  translate_requested: boolean;
  translated: boolean;
  source_language?: string | null;
  /** AI-extracted timeline lifecycle. Cheap to poll; the events
   *  themselves live behind GET /videos/:id/timeline. */
  timeline_status: VideoTimelineStatus;
  timeline_event_count: number;
  timeline_generated_at?: string | null;
  timeline_error?: string | null;
  /** Signed URL the bare <video src> can hit directly. Absent until
   *  status='processing' (the source bytes are in canonical storage). */
  play_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VideoStatusCounts {
  all: number;
  uploading: number;
  queued: number;
  processing: number;
  ready: number;
  failed: number;
}

export interface VideoPage {
  items: Video[];
  total: number;
  limit: number;
  offset: number;
  status_counts: VideoStatusCounts;
}

export interface VideoScene {
  id: number;
  video_id: number;
  scene_index: number;
  start_seconds: number;
  end_seconds: number;
  keyframe_url?: string | null;
  ai_summary?: string | null;
}

export interface VideoTranscriptSegment {
  id: number;
  video_id: number;
  segment_index: number;
  start_seconds: number;
  end_seconds: number;
  speaker_label?: string | null;
  /** Human-friendly speaker name (per-video override). When null the
   *  SPA falls back to rendering "Speaker N+1" from the integer in
   *  speaker_label (e.g. spk_2 -> "Speaker 3"). */
  speaker_display_name?: string | null;
  text: string;
}

export interface VideoSpeaker {
  speaker_label: string;
  display_name?: string | null;
  segment_count: number;
}

export interface VideoTimelineEvent {
  id: number;
  video_id: number;
  case_id: number;
  event_index: number;
  at_seconds: number;
  end_seconds?: number | null;
  label: string;
  description?: string | null;
  category: VideoTimelineCategory;
  importance: number;
  source_quote?: string | null;
  source_segment_ids: number[];
  source_scene_indices: number[];
  model?: string | null;
  is_favorite?: boolean;
  created_at: string;
  updated_at: string;
}

export interface VideoChatHistoryMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  citations: VideoChatCitation[];
  created_at: string;
}

export interface VideoChatHistory {
  video_id: number;
  thread_id: number | null;
  messages: VideoChatHistoryMessage[];
}

export interface VideoTimeline {
  video_id: number;
  status: VideoTimelineStatus;
  error?: string | null;
  generated_at?: string | null;
  model?: string | null;
  items: VideoTimelineEvent[];
}

export interface VideoUploadSession {
  upload_id: string;
  chunk_size: number;
  expected_chunks: number;
  received_indices: number[];
  received_bytes: number;
}

export interface VideoUploadStatus {
  upload_id: string;
  status: "open" | "complete" | "aborted";
  chunk_size: number;
  expected_chunks: number;
  received_indices: number[];
  received_bytes: number;
  total_bytes: number;
  video_id?: number | null;
}

// ---------- OneDrive integration ----------

export interface OneDriveStatus {
  /** Is the Azure app configured server-side at all? */
  configured: boolean;
  /** Has the current user linked their Microsoft account? */
  connected: boolean;
  account_email?: string | null;
  account_name?: string | null;
}

export interface OneDriveItem {
  id: string;
  name: string;
  is_folder: boolean;
  mime_type?: string | null;
  size_bytes?: number | null;
  child_count?: number | null;
  last_modified?: string | null;
  drive_id?: string | null;
  web_url?: string | null;
  /** True when this file maps to a supported document/video pipeline. */
  importable: boolean;
  import_kind?: "document" | "video" | null;
}

export interface OneDriveBrowse {
  items: OneDriveItem[];
  folder_id?: string | null;
}

export type OneDriveImportKind = "document" | "video";
export type OneDriveImportStatus =
  | "queued"
  | "downloading"
  | "imported"
  | "duplicate"
  | "failed";

export interface OneDriveImport {
  id: number;
  case_id: number;
  original_name: string;
  mime_type?: string | null;
  size_bytes?: number | null;
  kind: OneDriveImportKind;
  status: OneDriveImportStatus;
  progress_pct: number;
  document_id?: number | null;
  video_id?: number | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OneDriveImportList {
  items: OneDriveImport[];
}

/** One file the user picked, sent to POST /cases/:id/imports/onedrive. */
export interface OneDriveImportItemIn {
  item_id: string;
  drive_id?: string | null;
  name: string;
  mime_type?: string | null;
  size_bytes?: number | null;
}

export interface Citation {
  provenance_token: string;
  /** 'document' | 'video' — which section the evidence lives in. */
  source_type?: "document" | "video";
  /** 'text' | 'image' | 'audio' — drives the click-through renderer. */
  modality?: "text" | "image" | "audio";
  /** Document/image locator (null for video citations). */
  document_id?: number | null;
  document_name?: string | null;
  page?: number | null;
  paragraph?: number | null;
  /** Video locator (null for document/image citations). */
  video_id?: number | null;
  at_seconds?: number | null;
  end_seconds?: number | null;
  text: string;
  score: number;
}

export interface TimelineEvent {
  id?: number;
  event_date?: string | null;
  event_datetime?: string | null;
  date_precision: "year" | "month" | "day" | "datetime" | "unknown";
  headline: string;
  description?: string | null;
  actors: { name: string; role?: string }[];
  provenance_token: string;
  classification: "VERIFIED_FACT" | "SUPPORTED_INFERENCE" | "IDENTIFIED_GAP";
  confidence: number;
}

export interface QueryResponse {
  query_id: number;
  question: string;
  mode: "qa" | "timeline" | "both" | "insights";
  answer?: string | null;
  timeline: TimelineEvent[];
  citations: Citation[];
  latency_ms: number;
  tokens_in?: number | null;
  tokens_out?: number | null;
}

export type QueryStatus = "pending" | "queued" | "running" | "complete" | "failed";

export interface QuerySubmitResponse {
  query_id: number;
  conversation_id: number;
  status: QueryStatus;
  queue_position: number;
}

export interface QueryStatusResponse {
  query_id: number;
  case_id: number;
  conversation_id?: number | null;
  question: string;
  mode: "qa" | "timeline" | "both" | "insights";
  status: QueryStatus;
  progress_step?: string | null;
  progress_pct: number;
  queue_position: number;
  answer?: string | null;
  timeline: TimelineEvent[];
  citations: Citation[];
  latency_ms?: number | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
  error_message?: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

// ---------- Notes ----------

export type NoteSourceType = "manual" | "chat" | "timeline";
export type NoteScope = "mine" | "shared" | "all";

export interface NoteShareUser {
  user_id: number;
  display_name: string;
  email: string;
  shared_at: string;
}

export interface Note {
  id: number;
  case_id: number;
  user_id: number;
  owner_display_name?: string | null;
  owner_email?: string | null;
  title?: string | null;
  body: string;
  category?: string | null;
  tags: string[];
  source_type: NoteSourceType;
  source_ref?: Record<string, unknown> | null;
  is_owner: boolean;
  is_shared_with_me: boolean;
  shared_with: NoteShareUser[];
  created_at: string;
  updated_at: string;
}

/** Per-scope unfiltered totals for the notes toolbar chips. */
export interface NoteScopeCounts {
  all: number;
  mine: number;
  shared: number;
}

/** Paged response envelope from `GET /cases/:id/notes`. */
export interface NotePage {
  items: Note[];
  total: number;
  limit: number;
  offset: number;
  counts: NoteScopeCounts;
}

export interface CaseUser {
  id: number;
  display_name: string;
  email: string;
  /** The user's case role (`case_users.role`), NOT their account role. */
  role: CaseMemberRole;
  assigned_at?: string;
}

export interface Conversation {
  id: number;
  case_id: number;
  user_id?: number | null;
  owner_display_name?: string | null;
  owner_email?: string | null;
  title?: string | null;
  /** True iff the chat's owner has opted in to share with the rest of the case team. */
  is_shared: boolean;
  /** True iff the current viewer is the chat's owner (or it's a legacy ownerless row). */
  is_owner: boolean;
  message_count: number;
  last_question?: string | null;
  last_message_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** Paged response envelope from `GET /cases/:id/chats/search`. */
export interface ConversationPage {
  items: Conversation[];
  total: number;
  limit: number;
  offset: number;
}

/** Sidebar / chat-search visibility scope. */
export type ChatScope = "mine" | "shared" | "all";

export interface ChatOwner {
  id: number;
  display_name: string;
  email: string;
}

// ---------- Organizations ----------

export type OrgCategory =
  | "legal"
  | "automotive"
  | "research"
  | "corporate"
  | "healthcare"
  | "finance"
  | "education"
  | "government"
  | "technology"
  | "real-estate"
  | "insurance"
  | "consulting"
  | "nonprofit"
  | "other";

export const ORG_CATEGORY_LABELS: Record<OrgCategory, string> = {
  legal: "Legal",
  automotive: "Automotive",
  research: "Research",
  corporate: "Corporate",
  healthcare: "Healthcare",
  finance: "Finance",
  education: "Education",
  government: "Government",
  technology: "Technology",
  "real-estate": "Real Estate",
  insurance: "Insurance",
  consulting: "Consulting",
  nonprofit: "Nonprofit",
  other: "Other",
};

export interface Organization {
  id: number;
  name: string;
  slug: string;
  category: OrgCategory | null;
  /** Deactivating an org blocks its users from signing in and stops billing. */
  is_active: boolean;
  created_at: string;
  user_count: number;
}

// ---------- Activity log ----------

export interface ActivityEvent {
  id: number;
  ts: string;
  organization_id: number;
  actor_user_id?: number | null;
  actor_email?: string | null;
  actor_display_name?: string | null;
  event_type: string;
  event_label: string;
  case_id?: number | null;
  case_name?: string | null;
  target_type?: string | null;
  target_id?: number | null;
  target_label?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface ActivityPage {
  items: ActivityEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface ActivityFacetEvent {
  event_type: string;
  event_label: string;
  count: number;
}

export interface ActivityFacetUser {
  user_id?: number | null;
  display_name?: string | null;
  email?: string | null;
  count: number;
}

export interface ActivityFacetCase {
  case_id: number;
  case_name?: string | null;
  count: number;
}

export interface ActivityFacets {
  event_types: ActivityFacetEvent[];
  actors: ActivityFacetUser[];
  cases: ActivityFacetCase[];
}

// ---------- Time entries ----------

export interface TimeEntry {
  id: number;
  organization_id: number;
  user_id: number;
  user_display_name?: string | null;
  user_email?: string | null;
  case_id?: number | null;
  case_name?: string | null;
  entry_date: string;
  minutes: number;
  mileage?: number | null;
  note?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TimeEntryPage {
  items: TimeEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface TimeReportRow {
  user_id: number;
  user_display_name: string;
  user_email: string;
  total_minutes: number;
  total_mileage: number;
  entry_count: number;
}

export interface TimeReportResponse {
  rows: TimeReportRow[];
  period_start: string;
  period_end: string;
  total_minutes: number;
  total_mileage: number;
}

// ---------- Admin: site-wide model settings ----------

export type ModelProvider = "anthropic" | "openai" | "gemini";

export interface ModelSetting {
  task_key: string;
  provider: ModelProvider;
  model: string;
  max_tokens: number | null;
  temperature: number | null;
  description: string | null;
  updated_at: string | null;
  updated_by_id: number | null;
  updated_by_name: string | null;
  updated_by_email: string | null;
  is_fallback: boolean;
  // Per-slot constraints. When present, the UI restricts this slot's
  // provider + model dropdowns to these values (overriding the global
  // catalog). When null/undefined, the slot uses the global catalog.
  allowed_providers?: ModelProvider[] | null;
  allowed_models?: Partial<Record<ModelProvider, string[]>> | null;
}

export interface ModelCatalogEntry {
  provider: ModelProvider;
  models: string[];
}

export interface ModelSettingsList {
  settings: ModelSetting[];
  catalog: ModelCatalogEntry[];
  providers: ModelProvider[];
}

export interface ModelSettingUpdate {
  provider: ModelProvider;
  model: string;
  max_tokens: number | null;
  temperature: number | null;
}

// ---------- Admin: AI usage log ----------

export interface AiUsageEvent {
  id: number;
  ts: string;
  organization_id?: number | null;
  organization_name?: string | null;
  user_id?: number | null;
  user_display_name?: string | null;
  user_email?: string | null;
  case_id?: number | null;
  case_name?: string | null;
  feature: string;
  feature_label: string;
  task_key?: string | null;
  provider: string;
  model: string;
  call_kind: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
  audio_seconds?: number | null;
  cost_usd?: number | null;
  input_per_1m_usd?: number | null;
  output_per_1m_usd?: number | null;
  audio_per_minute_usd?: number | null;
  duration_ms?: number | null;
  success: boolean;
  error_message?: string | null;
  request_ref_type?: string | null;
  request_ref_id?: number | null;
  request_ref_label?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface AiUsagePage {
  items: AiUsageEvent[];
  total: number;
  limit: number;
  offset: number;
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_audio_seconds: number;
  total_calls: number;
}

export interface AiUsageGrandTotals {
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_audio_seconds: number;
  total_calls: number;
  success_calls: number;
  failed_calls: number;
}

export interface AiUsageBreakdownRow {
  key: string;
  label: string;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  audio_seconds: number;
  call_count: number;
  organization_id?: number | null;
  case_id?: number | null;
  user_id?: number | null;
}

export interface AiUsageDayPoint {
  day: string;
  cost_usd: number;
  call_count: number;
}

export interface AiUsageSummary {
  totals: AiUsageGrandTotals;
  by_feature: AiUsageBreakdownRow[];
  by_model: AiUsageBreakdownRow[];
  by_organization: AiUsageBreakdownRow[];
  by_case: AiUsageBreakdownRow[];
  by_user: AiUsageBreakdownRow[];
  by_day: AiUsageDayPoint[];
}

export interface AiUsageFacetOrg {
  organization_id?: number | null;
  name?: string | null;
  count: number;
  cost_usd: number;
}

export interface AiUsageFacetUser {
  user_id?: number | null;
  display_name?: string | null;
  email?: string | null;
  count: number;
  cost_usd: number;
}

export interface AiUsageFacetCase {
  case_id?: number | null;
  case_name?: string | null;
  organization_id?: number | null;
  count: number;
  cost_usd: number;
}

export interface AiUsageFacetFeature {
  feature: string;
  feature_label: string;
  count: number;
  cost_usd: number;
}

export interface AiUsageFacetModel {
  model: string;
  provider: string;
  count: number;
  cost_usd: number;
}

export interface AiUsageFacets {
  organizations: AiUsageFacetOrg[];
  users: AiUsageFacetUser[];
  cases: AiUsageFacetCase[];
  features: AiUsageFacetFeature[];
  models: AiUsageFacetModel[];
}

// ---------- Credit billing ----------

/** A pricing plan. Effective $/credit and the margin figures are all derived
 *  server-side, never stored, so they can't drift from the source numbers. */
export interface PricingTier {
  id: number;
  name: string;
  slug: string;
  base_price_usd: number;
  included_credits: number;
  overage_rate_usd: number;
  /** Storage allowance in GB (1 GB = 1 GiB), billed on each period's peak. */
  included_storage_gb: number;
  storage_overage_usd_per_gb: number;
  /** Recorded from the published matrix but not yet applied by the sweeper. */
  rollover_months: number;
  /** Informational only: all billing runs monthly. */
  annual_discount_pct: number;
  is_active: boolean;
  sort_order: number;
  notes: string | null;
  effective_per_credit_usd: number | null;
  org_count: number;
  /** Only ACTIVE orgs block deletion. */
  active_org_count: number;
  /** What this tier's customers are storing right now, summed. */
  stored_gb: number;
  /** Measured from closed invoices, null until there is data. */
  cost_per_credit: number | null;
  margin_multiple: number | null;
  unpriced_calls: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface PricingTierInput {
  name: string;
  slug: string;
  base_price_usd: number;
  included_credits: number;
  overage_rate_usd: number;
  included_storage_gb?: number;
  storage_overage_usd_per_gb?: number;
  rollover_months?: number;
  annual_discount_pct?: number;
  is_active?: boolean;
  sort_order?: number;
  notes?: string | null;
}

/** 'over' means the org has burned past its allowance and is accruing overage. */
export type CreditAlertState = "ok" | "warning" | "over";

export interface OrgBilling {
  organization_id: number;
  organization_name: string;
  is_active: boolean;
  /** False when the org has no billing settings row at all. */
  on_plan: boolean;
  tier_id: number | null;
  tier_name: string | null;
  billing_start_date: string | null;
  /** Effective values after per-org overrides are applied. */
  included_credits: number;
  base_price_usd: number;
  overage_rate_usd: number;
  included_storage_gb: number;
  storage_overage_usd_per_gb: number;
  alert_threshold_pct: number;
  /** Raw overrides: null means "inherit from the tier". */
  monthly_credits_override: number | null;
  base_price_override: number | null;
  overage_rate_override: number | null;
  storage_gb_override: number | null;
  storage_rate_override: number | null;
  notes: string | null;
  period_start: string | null;
  period_end: string | null;
  credits_used: number;
  /** Signed: negative once into overage. */
  remaining: number;
  overage_credits: number;
  pct_used: number;
  alert_state: CreditAlertState;
  /** Live reading; `peak_storage_gb` is the higher-water mark that bills. */
  storage_gb: number;
  peak_storage_gb: number;
  storage_overage_gb: number;
  storage_overage_amount_usd: number;
  storage_pct_used: number;
  storage_alert_state: CreditAlertState;
  lifetime_credits: number;
  lifetime_overage_credits: number;
  lifetime_overage_usd: number;
  lifetime_storage_overage_gb: number;
  lifetime_storage_overage_usd: number;
  /** How many billing periods this org has gone over. Upgrade signal. */
  overage_periods: number;
  cost_per_credit: number | null;
  margin_multiple: number | null;
  unpriced_calls: number;
}

export interface OrgBillingUpdate {
  tier_id?: number | null;
  billing_start_date?: string | null;
  monthly_credits?: number | null;
  base_price_usd?: number | null;
  overage_rate_usd?: number | null;
  included_storage_gb?: number | null;
  storage_overage_usd_per_gb?: number | null;
  alert_threshold_pct?: number | null;
  notes?: string | null;
}

/** One row of the editable credit menu. Global, not per-tier. */
export interface CreditWeight {
  action_key: string;
  label: string;
  unit: string;
  credits_per_unit: number;
  is_billable: boolean;
  notes: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
}

export type InvoiceStatus = "draft" | "approved" | "sent" | "paid" | "void";

export const INVOICE_STATUSES: InvoiceStatus[] = [
  "draft",
  "approved",
  "sent",
  "paid",
  "void",
];

export interface Invoice {
  id: number;
  invoice_number: string;
  organization_id: number | null;
  organization_name: string | null;
  period_id: number | null;
  period_start: string;
  period_end: string;
  tier_name: string | null;
  base_amount_usd: number;
  included_credits: number;
  credits_used: number;
  overage_credits: number;
  overage_rate_usd: number;
  overage_amount_usd: number;
  /** Storage line: the period's peak usage against its GB allowance. */
  included_storage_gb: number;
  peak_storage_gb: number;
  /** Which day produced the peak, so the charge can be checked. */
  peak_storage_on: string | null;
  storage_overage_gb: number;
  storage_overage_usd_per_gb: number;
  storage_overage_amount_usd: number;
  total_amount_usd: number;
  currency: string;
  /** Our measured AI spend for the period, frozen at close. */
  upstream_cost_usd: number | null;
  /** Calls whose model was missing from the rate card, so cost is understated. */
  unpriced_calls: number;
  status: InvoiceStatus;
  generated_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  notes: string | null;
  cost_per_credit: number | null;
  revenue_per_credit: number | null;
  margin_multiple: number | null;
}

/** Per-action credit consumption, computed from the ledger on demand. */
export interface CreditLine {
  action_key: string;
  label: string;
  unit: string;
  quantity: number;
  credits: number;
  events: number;
}

export interface InvoiceDetail extends Invoice {
  lines: CreditLine[];
}

/** A customer's view of their own invoice, from `/credits/invoices`.
 *  Narrower than `Invoice` on purpose: the server never sends our upstream
 *  cost, margin, or internal notes to a tenant. */
export interface CustomerInvoice {
  id: number;
  invoice_number: string;
  period_start: string;
  period_end: string;
  tier_name: string | null;
  base_amount_usd: number;
  included_credits: number;
  credits_used: number;
  overage_credits: number;
  overage_rate_usd: number;
  overage_amount_usd: number;
  included_storage_gb: number;
  peak_storage_gb: number;
  peak_storage_on: string | null;
  storage_overage_gb: number;
  storage_overage_usd_per_gb: number;
  storage_overage_amount_usd: number;
  total_amount_usd: number;
  currency: string;
  /** Only ever approved, sent, or paid - drafts and voids are filtered out. */
  status: InvoiceStatus;
  issued_at: string | null;
}

export interface CustomerInvoiceDetail extends CustomerInvoice {
  lines: CreditLine[];
}

export interface CreditBalance {
  on_plan: boolean;
  organization_id: number;
  tier_name: string | null;
  included_credits: number;
  credits_used: number;
  /** Signed: goes negative rather than blocking work. */
  remaining: number;
  overage_credits: number;
  pct_used: number;
  period_start: string | null;
  period_end: string | null;
  days_remaining: number;
  alert_threshold_pct: number;
  alert_state: CreditAlertState;
  overage_rate_usd: number;
  overage_amount_usd: number;
  /** Storage allowance and usage. `storage_gb` is what is stored right now;
   *  `peak_storage_gb` is the highest reading this period and what bills. */
  included_storage_gb: number;
  storage_gb: number;
  peak_storage_gb: number;
  storage_overage_gb: number;
  storage_overage_usd_per_gb: number;
  storage_overage_amount_usd: number;
  storage_pct_used: number;
  storage_alert_state: CreditAlertState;
  breakdown: CreditLine[];
  /** Up to 90 daily samples, oldest first. */
  storage_history: StorageSample[];
}

/** One day's storage reading for an organization. */
export interface StorageSample {
  captured_on: string;
  document_bytes: number;
  video_bytes: number;
  total_bytes: number;
  total_gb: number;
  document_count: number;
  video_count: number;
}

export interface MarginRow {
  tier_name?: string;
  organization_id?: number;
  organization_name?: string;
  invoices: number;
  credits_used: number;
  revenue_usd: number;
  cost_usd: number | null;
  unpriced_calls: number;
  cost_per_credit: number | null;
  revenue_per_credit: number | null;
  margin_multiple: number | null;
}

export interface MarginReport {
  by_tier: MarginRow[];
  by_organization: MarginRow[];
  totals: MarginRow;
}

export interface SweepResult {
  orgs_checked: number;
  periods_opened: number;
  periods_closed: number;
  invoices_created: number;
  errors: number;
}

/** How a month is defined in the usage report. `period` follows each customer's
 *  own billing anniversary and is the only grouping where overage is defined;
 *  `month` is calendar months, comparable across customers but straddling
 *  billing cycles, so the allowance fields come back null. */
export type UsageGroupBy = "period" | "month";

export type CreditAlert = "ok" | "warning" | "over" | "unknown";

export interface UsageRow {
  organization_id: number;
  organization_name: string;
  is_active: boolean;
  bucket: string;
  period_id: number | null;
  period_start: string;
  period_end: string;
  status: "open" | "closed" | null;
  tier_name: string | null;
  // Null whenever there is no allowance to compare against: calendar-month
  // grouping, or a single-action filter that makes credits a subset.
  included_credits: number | null;
  credits_used: number;
  events: number | null;
  remaining: number | null;
  overage_credits: number | null;
  overage_rate_usd: number;
  overage_amount_usd: number | null;
  pct_used: number | null;
  alert_state: CreditAlert;
  alert_threshold_pct: number;
  // Peak survives calendar-month grouping (samples are dated); the allowance
  // and overage do not, since those belong to a billing cycle.
  included_storage_gb: number | null;
  peak_storage_gb: number | null;
  storage_overage_gb: number | null;
  storage_overage_amount_usd: number | null;
  invoice_id: number | null;
  invoice_number: string | null;
  invoice_status: InvoiceStatus | null;
  days_elapsed: number | null;
  days_total: number | null;
  projected_credits: number | null;
  projected_overage_credits: number | null;
  projected_overage_amount_usd: number | null;
}

export interface UsageActionRow {
  action_key: string;
  label: string;
  unit: string;
  quantity: number;
  credits: number;
  events: number;
  organizations: number;
}

export interface UsageTotals {
  rows: number;
  organizations: number;
  credits_used: number;
  overage_credits: number;
  overage_amount_usd: number;
  storage_overage_gb: number;
  storage_overage_amount_usd: number;
  orgs_over: number;
  orgs_warning: number;
  orgs_projected_over: number;
  allowance_comparable: boolean;
}

export interface UsageReport {
  group_by: UsageGroupBy;
  action_key: string | null;
  rows: UsageRow[];
  by_action: UsageActionRow[];
  totals: UsageTotals;
}

// Friendly labels for the task slot ids returned by /api/admin/models. The
// backend doesn't ship these because it tries to stay UI-agnostic; the
// names below mirror the taxonomy on the Models settings page.
export const TASK_LABELS: Record<string, { title: string; blurb: string }> = {
  chat_qa: {
    title: "Chat — Strict Q&A / Timeline",
    blurb:
      "The grounded answer model. Used for question/timeline/both modes; every claim must cite an evidence token.",
  },
  chat_insights: {
    title: "Chat — Insights",
    blurb:
      "Exploratory brainstorming over a wider net of evidence. Higher temperature; reasoning is allowed beyond literal text.",
  },
  timeline_extract: {
    title: "Document Ingest — Timeline Extraction",
    blurb:
      "Per-chunk extraction of dated events during document ingestion. Runs once per chunk so cheap models are usually best.",
  },
  video_vision: {
    title: "Video Ingest — Scene Captioning",
    blurb:
      "Vision call against each detected scene's keyframe to write a short factual caption. OpenAI vision-capable chat models only (gpt-4o family).",
  },
  video_transcribe: {
    title: "Video Ingest — Audio Transcription",
    blurb:
      "Whisper-style transcription of the video's audio track with segment timestamps. OpenAI Whisper / gpt-4o-transcribe family only.",
  },
};
